import { ProfileCompletionFields } from 'src/api/profile-completion';
import {
  AccountCancellationRequestStatus,
  AuditableEntity,
  ContactAgentAllowedTargets,
  FileData,
  FullName,
  LocationInfo,
  MonthYearOnly,
  PhoneNumber,
  ProfileImage,
  UserIdentity,
  UserIdentityDto,
  FileDataViewModel
} from '../common';
import { JobDomain } from '../taxonomies/job-domains-api';
import { JobRole } from '../taxonomies/job-roles-api';
import { JobSkill } from '../taxonomies/job-skills-api';
import { Language } from '../taxonomies/languages-api';
import { Methodology } from '../taxonomies/job-methodologies-api';
import { EnterpriseUser } from '../enterprise-profile/types';
import { J2LContract } from '../contract/types';

export interface Experience {
  yearsGreatEqThan: number;
  yearsLessEqThan?: number;
}

export interface PersonalUrl {
  url: string;
  visibleToClients?: boolean;
}

export interface PersonJobMethodology extends AuditableEntity<string> {
  personProfileId: string;
  methodologyId: string;
  methodology: Methodology;
  experience: Experience;
  favorite: boolean;
  order: number;
}

export interface PersonJobRole extends AuditableEntity<string> {
  personProfileId: string;
  roleId: string;
  role: JobRole;
  experience: Experience;
  favorite: boolean;
  order: number;
}

export interface PersonReview extends AuditableEntity<string> {
  id: string;
  executionRating: number;
  teamWorkingRating: number;
  commitmentRating: number;
  comment: string | undefined;
  verifiedComment: boolean;
  enterpriseUser: EnterpriseUser;
  contract: J2LContract;
}

export interface PersonJobRoleSkill extends AuditableEntity<string> {
  personProfileId: string;
  roleId: string;
  skillId: string;
  skill: JobSkill;
  experience: Experience;
  favorite: boolean;
  order: number;
}

export type LanguageLevel = 'Elementary' | 'LimitedWorking' | 'FullProfessionalWorking' | 'NativeOrBilingual';

export interface PersonLanguageLevel extends AuditableEntity<string> {
  personProfileId: string;
  languageId: number;
  language: Language;
  level: LanguageLevel;
  order: number;
}

/**
 * Los campos de completitud (issue #1774) los aporta `ProfileCompletionFields`, que los declara opcionales:
 * OData solo devuelve lo que se le pide, asi que solo llegan si la consulta los incluye en el .
 */
export interface PersonProfile extends AuditableEntity<string>, ProfileCompletionFields {
  userIdentity: UserIdentity;
  fullName: FullName;
  publicFullName: FullName;
  userIdentityId: string;
  domainId: string;
  domain: JobDomain;
  nickName: string;
  phoneNumber: PhoneNumber;
  personalUrl: PersonalUrl;
  publicPersonalUrl: PersonalUrl;
  workLocation: LocationInfo;
  serviceDefinitionPhrase: string;
  serviceDefinition: string;
  profileImage: ProfileImage;
  roles: PersonJobRole[];
  skills: PersonJobRoleSkill[];
  methodologies: PersonJobMethodology[];
  languages: PersonLanguageLevel[];
  professionalExperiences: ProfessionalExperience[];
  showEnterpriseNameOnProfessionalExperiences: boolean;
  educationalQualifications: EducationalQualification[];
  certifications: Certification[];
  availabilitySlots: AvailabilitySlot[];
  meetingAvailabilitySlot?: MeetingAvailabilitySlot;
  ratingAverages: PersonRatingAverage;
  receiveEmailNotifications: boolean;
  accountCancellationRequest: PersonAccountCancellationRequest;
  cancellationRequestStatus: AccountCancellationRequestStatus;
  rejectedCancellationRequestComment: string;
  onboardingCompleted: boolean;
  contactAgentId: string;
  contactAgent: ContactAgent;
  professionalExperienceCompleted: boolean;
  educationalQualificationsCompleted: boolean;
  certificationCompleted: boolean;
  cvDocumentFileId: string;
  cvDocumentFile: FileData;
  linkedInUrl?: string;
}

export interface ContactAgent {
  id: string;
  email: string;
  phoneNumber: PhoneNumber;
  fullName: FullName;
  personUsers: PersonProfile[];
  enterpriseUsers: EnterpriseUser[];
  bannerImageFileId: string;
  bannerImageFile: FileData;
  meetingSchedulingUrl: string;
  allowedTargets: ContactAgentAllowedTargets;
}

export interface PersonRatingAverage {
  total: number;
  executionRating: number;
  teamWorkingRating: number;
  commitmentRating: number;
}

export interface UpsertPersonProfileJobRoleDto {
  id: string;
  label: string;
  order: number;
}

export interface UpsertPersonProfileJobRolesBody {
  user?: UserIdentityDto;
  roles: UpsertPersonProfileJobRoleDto[];
}

export interface UpsertPersonProfileJobRolesResult {
  roles: PersonJobRoleReferenceDto[];
  skills: PersonJobRoleSkillReferenceDto[];
  profileId: string;
}

export interface ExperienceDto {
  yearsGreatEqThan: number;
  yearsLessEqThan?: number;
}
export interface JobRoleReferenceDto {
  id: string;
  domainId: string;
  name: string;
}
export interface JobSkillReferenceDto {
  id: string;
  name: string;
}

export interface PersonJobRoleDto extends AuditableEntity<string> {
  personProfileId: string;
  roleId: string;
  role: JobRoleReferenceDto;
  experience: ExperienceDto;
  favorite: boolean;
  order: number;
}

export interface PersonJobRoleReferenceDto {
  id: string;
  personProfileId: string;
  roleId: string;
  roleName: string;
  experience: ExperienceDto;
  favorite: boolean;
  order: number;
}

export interface PersonJobRoleSkillDto extends AuditableEntity<string> {
  personProfileId: string;
  roleId: string;
  skillId: string;
  skill: JobSkillReferenceDto;
  experience: ExperienceDto;
  favorite: boolean;
  order: number;
}

export interface PersonJobRoleSkillReferenceDto {
  id: string;
  personProfileId: string;
  roleId: string;
  skillId: string;
  skillName: string;
  experience: ExperienceDto;
  favorite: boolean;
  order: number;
}

export interface UpsertPersonProfileJobSkillDto {
  id?: string;
  label: string;
  roleId: string;
  order: number;
}

export interface UpsertPersonProfileJobRoleSkillsBody {
  user?: UserIdentityDto;
  roleId?: string;
  skills: UpsertPersonProfileJobSkillDto[];
}

export interface UpsertPersonProfileJobSkillsResult {
  roleId?: string;
  skills: PersonJobRoleSkillReferenceDto[];
  profileId: string;
}

export interface UpsertPersonProfileMethodologyDto {
  id?: string;
  name: string;
  order: number;
  experience?: ExperienceDto;
  favorite?: boolean;
}

export interface UpsertPersonProfileMethodologiesBody {
  user?: UserIdentityDto;
  methodologies: UpsertPersonProfileMethodologyDto[];
}

export interface UpsertPersonProfileMethodologiesResult {
  methodologies: UpsertPersonProfileMethodologyDto[];
  profileId: string;
}

export interface UpsertPersonProfileExperienceDto {
  roleId: string;
  skillId?: string;
  methodologyId?: string;
  favorite: boolean;
  ge: number;
  le?: number;
}

export interface UpsertPersonProfileExperiencesBody {
  user?: UserIdentityDto;
  experiences: UpsertPersonProfileExperienceDto[];
}

export interface UpsertPersonProfileExperiencesResult {
  experiences: UpsertPersonProfileExperienceDto[];
  profileId: string;
}

export interface UpsertPersonProfileLanguageDto {
  languageId: number;
  level: LanguageLevel;
  order: number;
}

export interface UpsertPersonProfileLanguagesBody {
  user?: UserIdentityDto;
  languages: UpsertPersonProfileLanguageDto[];
}

export interface UpsertPersonProfileLanguagesResult {
  languages: UpsertPersonProfileLanguageDto[];
  profileId: string;
}

export interface UpsertPersonProfileContactInfoDto {
  username: string;
  phoneNumber: string;
  phoneNumberCountryId: number;
  firstName?: string;
  lastName?: string;
  fullNameVisible?: boolean;
}

export interface UpsertPersonProfileContactAndProfessionalInfoBody {
  user?: UserIdentityDto;
  contactInfo: UpsertPersonProfileContactInfoDto;
  professionalInfo: UpsertPersonProfileProfessionalInfoDto;
}

export interface UpsertPersonProfileContactAndProfessionalInfoResult {
  contactInfo: UpsertPersonProfileContactInfoDto;
  professionalInfo: UpsertPersonProfileProfessionalInfoDto;
  profileId: string;
}

export interface UpsertPersonProfileProfessionalInfoDto {
  location: LocationInfo;
  relatedUrl: string;
  shareRelatedUrl?: boolean;
}

export interface UpsertPersonProfileServiceInfoDto {
  serviceDefinitionPhrase: string;
  serviceDefinition: string;
}

export interface UpsertPersonProfileServiceInfoBody {
  user?: UserIdentityDto;
  serviceInfo: UpsertPersonProfileServiceInfoDto;
}

export interface UpsertPersonProfileServiceInfoResult {
  serviceInfo: UpsertPersonProfileServiceInfoDto;
  profileId: string;
}

export interface ProfessionalExperienceDto {
  id: string;
  personProfileId: string;
  order: number;
  role: string;
  projectName: string;
  enterpriseOrClient: string;
  publicEnterpriseOrClient: string;
  location: string;

  onGoing: boolean;

  from: MonthYearOnly;
  to: MonthYearOnly;

  description: string;
  relatedResourceUrl: string;
  relatedResourceLabel: string;
  relatedResourcePassword: string;
}

export interface ProfessionalExperience extends AuditableEntity<string>, ProfessionalExperienceDto {}

export interface AddProfessionalExperienceBody {
  profileId: string;
  role: string;
  order?: number;
  projectName: string;
  enterpriseOrClient: string;
  location: string;
  onGoing: boolean;

  fromMonth: number;
  fromYear: number;
  toMonth?: number;
  toYear?: number;
  description: string;
  relatedResourceUrl: string;
  relatedResourceLabel: string;
  relatedResourcePassword: string;
}

export interface UpdateProfessionalExperienceBody extends AddProfessionalExperienceBody {
  professionalExperienceId: string;
}

export interface ProfessionalExperienceOrder {
  id: string;
  order: number;
}
export interface ReorderProfessionalExperiencesBody {
  personProfileId: string;
  order: ProfessionalExperienceOrder[];
}

export interface EducationalQualificationDto {
  id: string;
  personProfileId: string;
  order: number;
  title: string;
  institution: string;
  onGoing: boolean;
  from: MonthYearOnly;
  to?: MonthYearOnly;
}

export interface EducationalQualification extends AuditableEntity<string>, EducationalQualificationDto {}

export interface AddEducationalQualificationBody {
  personProfileId: string;
  order?: number;
  title: string;
  institution: string;
  onGoing: boolean;
  fromYear: number;
  fromMonth: number;
  toYear?: number;
  toMonth?: number;
}

export interface EducationalQualificationOrder {
  id: string;
  order: number;
}
export interface ReorderEducationalQualificationsBody {
  personProfileId: string;
  order: EducationalQualificationOrder[];
}

export interface UpdateEducationalQualificationBody {
  personProfileId: string;
  id: string;
  order?: number;
  title: string;
  institution: string;
  onGoing: boolean;
  fromYear: number;
  fromMonth: number;
  toYear?: number;
  toMonth?: number;
}

export interface CertificationDto {
  id: string;
  personProfileId: string;
  order: number;
  title: string;
  institution: string;
  onGoing: boolean;
  date: MonthYearOnly;
  url: string;
}

export interface Certification extends AuditableEntity<string>, CertificationDto {}

export interface AddCertificationBody {
  personProfileId: string;
  order?: number;
  title: string;
  institution: string;
  onGoing: boolean;
  dateMonth?: number;
  dateYear?: number;
  url?: string;
}
export interface DeleteCertificationBody {
  personProfileId: string;
  id: string;
}
export interface UpdateCertificationBody {
  personProfileId: string;
  id: string;
  order?: number;
  title: string;
  institution: string;
  onGoing: boolean;
  dateMonth?: number;
  dateYear?: number;
  url?: string;
}

export interface CertificationOrder {
  id: string;
  order: number;
}
export interface ReorderCertificationsBody {
  personProfileId: string;
  order: CertificationOrder[];
}

export enum WorkingDayType {
  AdaptToProjectSchedule = 'AdaptToProjectSchedule',
  PartialEightHoursDay = 'PartialEightHoursDay',
  ContinuosEightHoursDay = 'ContinuosEightHoursDay',
  ContinuousSixHoursDay = 'ContinuousSixHoursDay',
  CustomDay = 'CustomDay'
}

export enum WorkingWeekType {
  MondayToFriday = 'MondayToFriday',
  FourDaysWeek = 'FourDaysWeek',
  Weekends = 'Weekends'
}

export enum ByLocationWorkTypePreference {
  None = 'None',
  RemoteOnly = 'RemoteOnly',
  HybridWithFewOfficeDays = 'HybridWithFewOfficeDays',
  HybridWithOneOfficeDayPerWeek = 'HybridWithOneOfficeDayPerWeek',
  HybridWithTwoOfficeDayPerWeeks = 'HybridWithTwoOfficeDayPerWeeks',
  HybridWithThreeOfficeDaysPerWeek = 'HybridWithThreeOfficeDaysPerWeek',
  HybridWithFourOfficeDaysPerWeek = 'HybridWithFourOfficeDaysPerWeek',
  OfficeOnly = 'OfficeOnly'
}

export enum ContractType {
  J2LPayroll = 'J2LPayroll',
  SelfEmployed = 'SelfEmployed'
}

export enum PersonCancelAccountReason {
  DidntFindInterestingProjects = 0,
  MyRoleIsNotCovered = 10,
  ServicesDontMatchMyConditions = 20,
  WasRejectedTooManyTimes = 30,
  TooManyNotifications = 40,
  DontSeeTheAdvantageOfUsingJobs2Live = 50,
  FoundBetterAlternatives = 60,
  OtherReason = 70
}

export type DateRangeType = 'Undefined' | 'Concrete' | 'NextMonths';

export interface HourRange {
  from: number;
  to: number;
}

export enum DayOfWeek {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday'
}

export interface DayHourRange {
  hourRange: HourRange;
  days: DayOfWeek[];
  mondayToFriday: boolean;
}

export enum AvailabilitySlotState {
  Open = 'Open',
  Finished = 'Finished'
}

export interface MeetingAvailabilitySlotDto {
  id: string;
  personProfileId: string;
  dayHourRanges: DayHourRange[];
}

export interface MeetingAvailabilitySlot extends AuditableEntity<string>, MeetingAvailabilitySlotDto {}

export interface EstimatedMonthlyIncomeInfo {
  /**total de horas trabajadas al mes */
  monthlyWorkingHours: number;

  /**ingreso bruto mensual trabajador */
  bruteIncome: number;

  /**porciento comisión jobs2live */
  j2lEnterpriseCommissionPercent: number;

  /**total mensual en comisión jobs2live */
  j2lEnterpriseCommissionCost: number;

  /**porciento IVA */
  ivaPercent: number;

  /**total mensual en porciento IVA */
  ivaCost: number;

  /**porciento de IRPF */
  irpfPercent: number;

  /**total mensual en IRPF */
  irpfCost: number;

  /**porciento en seguridad social del trabajador */
  workerSocialSecurityPercent: number;

  /**total mensual en seguridad social del trabajador */
  workerSocialSecurityCost: number;

  /**porciento en seguridad social de la empresa */
  enterpriseSocialSecurityPercent: number;

  /**total mensual en seguridad social de la empresa */
  enterpriseSocialSecurityCost: number;

  /**porciento de provisión por despido */
  dismissalProvisionPercent: number;

  /**total mensual en provisión por despido */
  dismissalProvisionCost: number;

  /**porciento de provisión por vacaciones */
  otherContingenciesPercent: number;

  /**total mensual de provisión por vacaciones */
  otherContingenciesCost: number;

  /**total mensual de costos a la empresa */
  totalEnterpriseCost: number;

  /**total mensual de impuestos al trabajador */
  totalWorkerCost: number;

  /**ingreso líquido mensual estimado al trabajador */
  estimatedIncome: number;

  /**costo mensual estimado a la empresa por el servicio */
  estimatedServiceCost: number;

  /**contribución mínima mensual trabajador */
  j2lPayrollWorkerMinMonthlyContribution: number;

  /**contribución máxima mensual trabajador */
  j2lPayrollWorkerMaxMonthlyContribution: number;

  /**contribución mínima mensual empresa */
  j2lPayrollEnterpriseMinMonthlyContribution: number;

  /**contribución máxima mensual empresa */
  j2lPayrollEnterpriseMaxMonthlyContribution: number;

  annualGrossSalary: number;

  /**indica si el cálculo fue exitoso */
  success: boolean;

  createdOn: string;
}

export interface AvailabilitySlotDto {
  id: string;
  personProfileId: string;
  title: string;
  withDateRange: boolean;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  isDateRangeNegotiable: boolean;
  hourlyRate: number;
  isHourlyRateNegotiable: boolean;
  workingWeekType: WorkingWeekType;
  isWorkingWeekTypeNegotiable: boolean;
  workingDayType: WorkingDayType;
  isWorkingDayTypeNegotiable: boolean;
  byLocationWorkTypePreference: ByLocationWorkTypePreference;
  isByLocationWorkTypePreferenceNegotiable: boolean;
  contractType: ContractType;
  meetingAvailabilitySlotId?: string;
  meetingAvailabilitySlot?: MeetingAvailabilitySlotDto;
  state: AvailabilitySlotState;
  disabled: boolean;
  estimatedMonthlyIncomeInfo: EstimatedMonthlyIncomeInfo;
}

export interface AvailabilitySlot extends AuditableEntity<string> {
  personProfileId: string;
  title: string;
  withDateRange: boolean;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  isDateRangeNegotiable: boolean;
  hourlyRate: number;
  isHourlyRateNegotiable: boolean;
  workingWeekType: WorkingWeekType;
  isWorkingWeekTypeNegotiable: boolean;
  workingDayType: WorkingDayType;
  isWorkingDayTypeNegotiable: boolean;
  customWorkingHoursPerWeek?: number;
  byLocationWorkTypePreference: ByLocationWorkTypePreference;
  isByLocationWorkTypePreferenceNegotiable: boolean;
  contractType: ContractType;
  hasAdvanceNoticeDate: boolean;
  state: AvailabilitySlotState;
  disabled: boolean;
  newMatchesCount: number;
  acceptedMatchesCount: number;
  rejectedMatchesCount: number;
  unavailableMatchesCount: number;
  selectedMatchesCount: number;
  matchMeetingsCount: number;
  contractsCount: number;
  newMatchesAddedOn?: string;
  notificationsFrequency: AvailabilitySlotNotificationFrequency;
  estimatedMonthlyIncomeInfo: EstimatedMonthlyIncomeInfo;
  estimatedVacationDays: number;
}

export interface UpsertMeetingAvailabilitySlotBody {
  dayHourRanges: DayHourRange[];
}

export interface CreateAvailabilitySlotBody {
  title: string;
  withDateRange: boolean;
  dateRangeStart: string;
  dateRangeEnd: string;
  isDateRangeNegotiable: boolean;
  hourlyRate: number;
  annualGrossSalary: number;
  isHourlyRateNegotiable: boolean;
  workingWeekType: WorkingWeekType;
  isWorkingWeekTypeNegotiable: boolean;
  workingDayType: WorkingDayType;
  isWorkingDayTypeNegotiable: boolean;
  customWorkingHoursPerWeek?: number;
  byLocationWorkTypePreference: ByLocationWorkTypePreference;
  isByLocationWorkTypePreferenceNegotiable: boolean;
  contractType: ContractType;
  hasAdvanceNoticeDate: boolean;
  notificationFrequency: AvailabilitySlotNotificationFrequency;
  meetingAvailabilitySlotId?: string;
  meetingAvailabilitySlot?: UpsertMeetingAvailabilitySlotBody;
}

export interface PersonMeetingAvailabilityInfo {
  error?: string;
  /** Both absent whenever `error` is set: the refusal carries only a message. */
  meetingAvailabilitySlot?: MeetingAvailabilitySlotDto;
  blockedDateTimes?: string[];
}

export interface UpsertPersonCvBody {
  cvDocumentChanged?: boolean;
  cvDocumentFile?: FileDataViewModel | null;
}

export enum AvailabilitySlotNotificationFrequency {
  Never = 'Never',
  OnceADay = 'OnceADay',
  OnEveryMatch = 'OnEveryMatch',
  OnceAWeek = 'OnceAWeek'
}

export interface PersonAccountCancellationRequest extends AuditableEntity<string> {
  comment: string;
  reason: PersonCancelAccountReason;
}

export enum ProfileSection {
  Certifications = 'Certifications',
  EducationalQualifications = 'EducationalQualifications',
  ProfessionalExperience = 'ProfessionalExperience'
}

export interface UpsertPersonCvDocumentsResponse {
  cvDocumentFileId: string;
  cvDocumentFileUrl: string;
}

export interface LanguageLevelDto {
  language: string;
  languageId: number;
  level: LanguageLevel;
  order?: number;
}

export interface AutofilledProfilePatchDto {
  fullName: FullName;
  phoneNumber: PhoneNumber;
  workLocation: LocationInfo;
  serviceDefinitionPhrase: string;
  serviceDefinition: string;
  languages: LanguageLevelDto[];
  certifications: CertificationDto[];
  education: EducationalQualificationDto[];
  experience: ProfessionalExperienceDto[];
  roles: PersonJobRoleReferenceDto[];
  skills: PersonJobRoleSkillReferenceDto[];
}

export type AutocompletedFieldId =
  'fullName' | 'phoneNumber' | 'workLocation' | 'serviceDefinition' | 'language' | 'certifications' | 'education' | 'experience' | 'roles' | 'skills';

export type AutocompletedField =
  | { id: 'fullName'; label: string; value: FullName }
  | { id: 'phoneNumber'; label: string; value: PhoneNumber }
  | { id: 'workLocation'; label: string; value: LocationInfo }
  | { id: 'serviceDefinition'; label: string; value: { serviceDefinition?: string; serviceDefinitionPhrase?: string } }
  | { id: 'language'; label: string; value: LanguageLevelDto[] }
  | { id: 'certifications'; label: string; value: CertificationDto[] }
  | { id: 'education'; label: string; value: EducationalQualificationDto[] }
  | { id: 'experience'; label: string; value: ProfessionalExperienceDto[] }
  | { id: 'roles'; label: string; value: PersonJobRoleReferenceDto[] }
  | { id: 'skills'; label: string; value: PersonJobRoleSkillReferenceDto[] };

export type AutocompletedFlags = {
  [K in AutocompletedFieldId]?: boolean;
};

export enum CvProcessingStatus {
  None = 'None',
  InProgress = 'InProgress',
  Success = 'Success',
  NoRelevantData = 'NoRelevantData',
  Failed = 'Failed'
}
